import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import ImageUploadField from '../components/ImageUploadField';
import { 
  Calendar as CalendarIcon, 
  MapPin, 
  Clock, 
  Users,
  Plus,
  ChevronLeft,
  ChevronRight,
  Info,
  Image as ImageIcon,
  Check,
  X,
  AlertCircle,
  Search,
  Star,
  Archive,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CalendarPage = () => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [associations, setAssociations] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date(2026, 0, 1));
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'
  
  const [formData, setFormData] = useState({
    association_id: '',
    title: '',
    description: '',
    date: '',
    end_date: '', // For multi-day events
    time: '',
    end_time: '',
    location: '',
    category: '',
    notice: '',
    image_url: '',
    submitter_type: 'association', // 'association', 'external', 'individual'
    submitter_name: '',
    submitter_email: '',
    submitter_phone: '',
    organization_name: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [activitiesRes, assocRes, blockedRes] = await Promise.all([
        axios.get(`${API_URL}/activities/public?year=2026`),
        axios.get(`${API_URL}/associations`),
        axios.get(`${API_URL}/activities/blocked-dates?year=2026`)
      ]);
      setActivities(activitiesRes.data.activities || []);
      setAssociations(assocRes.data);
      setBlockedDates(blockedRes.data.blocked_dates || []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDateSelect = (date) => {
    if (!date) return;
    const dateStr = date.toISOString().split('T')[0];
    setSelectedDate(dateStr);
    
    // Check if date is blocked — show clash alert with nearby suggestions
    if (blockedDates.includes(dateStr)) {
      const event = activities.find(a => a.date === dateStr);
      const bookedBy = event ? event.association_name || event.organization_name : 'another event';
      
      // Find 3 nearest available dates
      const nearby = [];
      for (let offset = 1; nearby.length < 3 && offset <= 30; offset++) {
        for (const dir of [-1, 1]) {
          const d = new Date(date);
          d.setDate(d.getDate() + (offset * dir));
          const ds = d.toISOString().split('T')[0];
          if (ds >= today && !blockedDates.includes(ds) && !nearby.includes(ds)) {
            nearby.push(ds);
            if (nearby.length >= 3) break;
          }
        }
      }
      
      const suggestions = nearby.length > 0
        ? `\n\nNearest available dates:\n${nearby.map(d => `• ${formatShortDate(d)}`).join('\n')}`
        : '';
      
      toast.error(`This date is already booked by ${bookedBy}${suggestions}`, {
        duration: 8000,
        description: 'Please select a different date for your event.',
      });
      return;
    }
    
    setFormData(prev => ({ ...prev, date: dateStr }));
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    
    // Validation based on submitter type
    if (formData.submitter_type === 'association' && !formData.association_id) {
      toast.error('Please select your association');
      return;
    }
    if (formData.submitter_type === 'external' && !formData.organization_name) {
      toast.error('Please enter your organization name');
      return;
    }
    if (formData.submitter_type !== 'association') {
      if (!formData.submitter_name) {
        toast.error('Please enter your name');
        return;
      }
      if (!formData.submitter_email) {
        toast.error('Please enter your email');
        return;
      }
    }
    if (!formData.date) {
      toast.error('Please select a date');
      return;
    }
    if (blockedDates.includes(formData.date)) {
      // Find nearby available dates
      const nearby = [];
      const baseDate = parseLocalDate(formData.date);
      for (let offset = 1; nearby.length < 3 && offset <= 30; offset++) {
        for (const dir of [-1, 1]) {
          const d = new Date(baseDate);
          d.setDate(d.getDate() + (offset * dir));
          const ds = d.toISOString().split('T')[0];
          if (ds >= today && !blockedDates.includes(ds) && !nearby.includes(ds)) {
            nearby.push(ds);
            if (nearby.length >= 3) break;
          }
        }
      }
      const suggestions = nearby.length > 0
        ? ` Try: ${nearby.map(d => formatShortDate(d)).join(', ')}`
        : '';
      toast.error(`Date already booked!${suggestions}`, { duration: 8000 });
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/activities/public-book`, formData);
      const bookingData = response.data;
      
      // Get organization name for confirmation page
      let orgName = formData.organization_name;
      if (formData.submitter_type === 'association' && formData.association_id) {
        const assoc = associations.find(a => a.id === formData.association_id);
        orgName = assoc ? assoc.name : 'Association';
      } else if (formData.submitter_type === 'individual') {
        orgName = `Individual: ${formData.submitter_name}`;
      }
      
      setBookingDialogOpen(false);
      
      // Show a prominent success toast with confirmation number
      toast.success(
        `Booking confirmed! Ref: ${bookingData.confirmation_number}`,
        { 
          duration: 10000,
          description: `${formData.title} on ${formatShortDate(formData.date)} — Pending admin approval`
        }
      );
      
      // Navigate to confirmation page with booking data
      navigate('/calendar/confirmation', {
        state: {
          confirmationNumber: bookingData.confirmation_number,
          title: formData.title,
          date: formData.date,
          endDate: formData.end_date,
          time: formData.time,
          endTime: formData.end_time,
          location: formData.location,
          category: formData.category,
          organizationName: orgName,
          submitterEmail: formData.submitter_email || bookingData.submitter_email
        }
      });
      
      // Reset form
      setFormData({
        association_id: '',
        title: '',
        description: '',
        date: '',
        end_date: '',
        time: '',
        end_time: '',
        location: '',
        category: '',
        notice: '',
        image_url: '',
        submitter_type: 'association',
        submitter_name: '',
        submitter_email: '',
        submitter_phone: '',
        organization_name: ''
      });
      setSelectedDate(null);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to submit booking';
      // If it's a clash error from backend, show helpful message with suggestions
      if (detail.includes('already booked')) {
        toast.error(detail, { 
          duration: 8000,
          description: 'Please select a different date from the calendar.'
        });
      } else {
        toast.error(detail, { duration: 6000 });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isDateBlocked = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return blockedDates.includes(dateStr);
  };

  const getEventForDate = (dateStr) => {
    return activities.find(a => a.date === dateStr);
  };

  // Parse date string as local date (not UTC) to avoid timezone issues
  // "2026-07-31" should always display as July 31, regardless of user's timezone
  const parseLocalDate = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split('-');
    return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  };

  const formatDate = (dateStr) => {
    const date = parseLocalDate(dateStr);
    if (!date) return '';
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatShortDate = (dateStr) => {
    const date = parseLocalDate(dateStr);
    if (!date) return '';
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric'
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Cultural Festival': 'bg-cameroon-red text-white',
      'Meeting': 'bg-cameroon-green text-white',
      'Workshop': 'bg-cameroon-yellow text-black',
      'Celebration': 'bg-purple-500 text-white',
      'Community': 'bg-blue-500 text-white',
      'Education': 'bg-indigo-500 text-white',
      'Sports': 'bg-orange-500 text-white',
      'Other': 'bg-gray-500 text-white',
    };
    return colors[category] || 'bg-gray-500 text-white';
  };

  const categories = [
    'Cultural Festival',
    'Meeting',
    'Workshop',
    'Celebration',
    'Community',
    'Education',
    'Sports',
    'Other'
  ];

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Split activities into upcoming and past
  const today = new Date().toISOString().split('T')[0];
  const upcomingActivities = activities.filter(a => (a.end_date || a.date) >= today).sort((a, b) => a.date.localeCompare(b.date));
  const pastActivities = activities.filter(a => (a.end_date || a.date) < today).sort((a, b) => b.date.localeCompare(a.date));
  const featuredUpcoming = upcomingActivities.filter(a => a.featured || a.status === 'approved');
  const [showPast, setShowPast] = useState(false);

  // Group upcoming activities by month
  const upcomingByMonth = upcomingActivities.reduce((acc, activity) => {
    const month = activity.date.substring(0, 7);
    if (!acc[month]) acc[month] = [];
    acc[month].push(activity);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold">
              2026 Cultural Calendar
            </h1>
            <p className="text-muted-foreground mt-1 text-sm sm:text-base">
              {upcomingActivities.length} upcoming &middot; {pastActivities.length} past &middot; Book your association&apos;s event
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2">
            <Link to="/calendar/guide">
              <Button variant="outline" className="w-full sm:w-auto border-cameroon-green text-cameroon-green hover:bg-cameroon-green/10" data-testid="calendar-guide-btn">
                <Info className="w-4 h-4 mr-2" />
                How It Works
              </Button>
            </Link>
            <Link to="/track-booking">
              <Button variant="outline" className="w-full sm:w-auto" data-testid="track-booking-btn">
                <Search className="w-4 h-4 mr-2" />
                Track Booking
              </Button>
            </Link>
            <Dialog open={bookingDialogOpen} onOpenChange={setBookingDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-cameroon-green hover:bg-cameroon-green/90 w-full sm:w-auto" data-testid="book-event-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Book an Event
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-serif text-xl">Book Your Event</DialogTitle>
                <p className="text-sm text-muted-foreground">
                  Open to CCA associations, external organizations, and individuals
                </p>
              </DialogHeader>
              
              <form onSubmit={handleBooking} className="space-y-4 mt-4">
                {/* Submitter Type */}
                <div className="space-y-2">
                  <Label>Who is submitting? *</Label>
                  <Select 
                    value={formData.submitter_type}
                    onValueChange={(value) => setFormData({ 
                      ...formData, 
                      submitter_type: value,
                      association_id: value === 'association' ? formData.association_id : ''
                    })}
                  >
                    <SelectTrigger data-testid="submitter-type">
                      <SelectValue placeholder="Select submitter type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="association">CCA Member Association</SelectItem>
                      <SelectItem value="external">External Organization/Association</SelectItem>
                      <SelectItem value="individual">Individual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* CCA Association Selector */}
                {formData.submitter_type === 'association' && (
                  <div className="space-y-2">
                    <Label>Select Association *</Label>
                    <Select 
                      value={formData.association_id}
                      onValueChange={(value) => setFormData({ ...formData, association_id: value })}
                    >
                      <SelectTrigger data-testid="booking-association">
                        <SelectValue placeholder="Choose your association" />
                      </SelectTrigger>
                      <SelectContent>
                        {associations.map((assoc) => (
                          <SelectItem key={assoc.id} value={assoc.id}>
                            {assoc.short_name} - {assoc.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* External Organization Fields */}
                {formData.submitter_type === 'external' && (
                  <div className="space-y-2">
                    <Label>Organization Name *</Label>
                    <Input
                      value={formData.organization_name}
                      onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                      placeholder="e.g., Montreal African Community"
                      required
                      data-testid="organization-name"
                    />
                  </div>
                )}

                {/* Contact Info for External/Individual */}
                {(formData.submitter_type === 'external' || formData.submitter_type === 'individual') && (
                  <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm font-medium">Contact Information</p>
                    <div className="space-y-2">
                      <Label>Your Name *</Label>
                      <Input
                        value={formData.submitter_name}
                        onChange={(e) => setFormData({ ...formData, submitter_name: e.target.value })}
                        placeholder="Full name"
                        required
                        data-testid="submitter-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={formData.submitter_email}
                        onChange={(e) => setFormData({ ...formData, submitter_email: e.target.value })}
                        placeholder="email@example.com"
                        required
                        data-testid="submitter-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone (Optional)</Label>
                      <Input
                        type="tel"
                        value={formData.submitter_phone}
                        onChange={(e) => setFormData({ ...formData, submitter_phone: e.target.value })}
                        placeholder="+1 (514) 555-0000"
                        data-testid="submitter-phone"
                      />
                    </div>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label>Select Date *</Label>
                  <div className="border rounded-lg p-3">
                    <Calendar
                      mode="single"
                      selected={formData.date ? parseLocalDate(formData.date) : undefined}
                      onSelect={(date) => {
                        if (date) {
                          // Format as YYYY-MM-DD using local date parts
                          const year = date.getFullYear();
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          const day = String(date.getDate()).padStart(2, '0');
                          const dateStr = `${year}-${month}-${day}`;
                          setFormData({ ...formData, date: dateStr });
                        }
                      }}
                      defaultMonth={new Date(2026, 0)}
                      disabled={(date) => {
                        // Format as YYYY-MM-DD using local date parts
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const day = String(date.getDate()).padStart(2, '0');
                        const dateStr = `${year}-${month}-${day}`;
                        return blockedDates.includes(dateStr) || date < new Date(2026, 0, 1) || date > new Date(2026, 11, 31);
                      }}
                      className="mx-auto"
                    />
                  </div>
                  {formData.date && (
                    <div className={`text-sm p-2 rounded-md ${blockedDates.includes(formData.date) ? 'bg-destructive/10 text-destructive' : 'bg-cameroon-green/10 text-cameroon-green'}`}>
                      {blockedDates.includes(formData.date) 
                        ? `⚠ ${formatDate(formData.date)} — This date is already booked!`
                        : `✓ ${formatDate(formData.date)} — Available`
                      }
                    </div>
                  )}
                </div>
                
                {/* Multi-day Event Option */}
                <div className="space-y-2 p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="multiday"
                      checked={!!formData.end_date}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData(prev => ({ ...prev, end_date: prev.date }));
                        } else {
                          setFormData(prev => ({ ...prev, end_date: '' }));
                        }
                      }}
                      className="rounded"
                    />
                    <Label htmlFor="multiday" className="text-sm cursor-pointer">
                      Multi-day event (Convention, Festival, etc.)
                    </Label>
                  </div>
                  
                  {formData.end_date && (
                    <div className="mt-3 space-y-2">
                      <Label className="text-xs text-muted-foreground">End Date</Label>
                      <Input
                        type="date"
                        value={formData.end_date}
                        min={formData.date}
                        onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                        className="w-full"
                        data-testid="booking-end-date"
                      />
                      {formData.date && formData.end_date && formData.end_date !== formData.date && (
                        <p className="text-xs text-cameroon-green">
                          Duration: {Math.ceil((parseLocalDate(formData.end_date) - parseLocalDate(formData.date)) / (1000*60*60*24)) + 1} days
                        </p>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label>Event Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Annual Cultural Festival"
                    required
                    data-testid="booking-title"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Description *</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe your event..."
                    rows={3}
                    required
                    data-testid="booking-description"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Time</Label>
                    <Input
                      type="time"
                      value={formData.time}
                      onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                      data-testid="booking-time"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Time</Label>
                    <Input
                      type="time"
                      value={formData.end_time}
                      onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                      data-testid="booking-end-time"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Location / Address</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="e.g., Montreal Community Center, 123 Main St"
                    data-testid="booking-location"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select 
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger data-testid="booking-category">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Notice / Additional Info</Label>
                  <Textarea
                    value={formData.notice}
                    onChange={(e) => setFormData({ ...formData, notice: e.target.value })}
                    placeholder="Any special notices or requirements..."
                    rows={2}
                    data-testid="booking-notice"
                  />
                </div>
                
                <div className="space-y-2">
                  <ImageUploadField
                    value={formData.image_url}
                    onChange={(url) => setFormData({ ...formData, image_url: url })}
                    label="Event Image (optional)"
                    placeholder="https://... or upload"
                    showPreview={true}
                    previewAspect="video"
                    requireAuth={false}
                  />
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setBookingDialogOpen(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-cameroon-green hover:bg-cameroon-green/90"
                    disabled={submitting}
                    data-testid="submit-booking"
                  >
                    {submitting ? 'Submitting...' : 'Submit for Approval'}
                  </Button>
                </div>
              </form>
            </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* View Toggle - Mobile */}
        <div className="flex gap-2 mb-6 sm:hidden">
          <Button 
            variant={viewMode === 'calendar' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('calendar')}
            className="flex-1"
          >
            <CalendarIcon className="w-4 h-4 mr-2" />
            Calendar
          </Button>
          <Button 
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('list')}
            className="flex-1"
          >
            <Users className="w-4 h-4 mr-2" />
            Events
          </Button>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Calendar Section */}
          <div className={`lg:col-span-1 ${viewMode === 'list' ? 'hidden' : 'block'} sm:block`}>
            <Card className="sticky top-20">
              <CardHeader className="pb-2">
                <CardTitle className="font-serif text-lg flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5 text-cameroon-green" />
                  Select a Date
                </CardTitle>
                <CardDescription>
                  Green dates are available, Red dates are booked
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Calendar
                  mode="single"
                  selected={selectedDate ? parseLocalDate(selectedDate) : undefined}
                  onSelect={handleDateSelect}
                  month={selectedMonth}
                  onMonthChange={setSelectedMonth}
                  defaultMonth={new Date(2026, 0)}
                  modifiers={{
                    booked: (date) => isDateBlocked(date),
                    pastDay: (date) => date < new Date(new Date().setHours(0,0,0,0)),
                  }}
                  modifiersStyles={{
                    booked: { 
                      backgroundColor: 'hsl(var(--destructive))', 
                      color: 'white',
                      fontWeight: 'bold',
                      opacity: 0.85,
                      textDecoration: 'line-through',
                      cursor: 'not-allowed',
                    },
                    pastDay: {
                      opacity: 0.35,
                    }
                  }}
                  className="mx-auto"
                />
                
                {/* Legend */}
                <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-primary"></div>
                    <span className="text-muted-foreground">Selected</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-destructive flex items-center justify-center">
                      <span className="text-white text-[8px] font-bold leading-none">X</span>
                    </div>
                    <span className="text-muted-foreground">Booked</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-cameroon-green/30"></div>
                    <span className="text-muted-foreground">Available</span>
                  </div>
                </div>
                
                {/* Quick Stats */}
                <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t">
                  <div className="text-center p-2 bg-cameroon-green/10 rounded-lg">
                    <div className="text-xl font-bold text-cameroon-green">{upcomingActivities.length}</div>
                    <div className="text-[10px] text-muted-foreground">Upcoming</div>
                  </div>
                  <div className="text-center p-2 bg-cameroon-red/10 rounded-lg">
                    <div className="text-xl font-bold text-cameroon-red">{blockedDates.length}</div>
                    <div className="text-[10px] text-muted-foreground">Booked</div>
                  </div>
                  <div className="text-center p-2 bg-muted rounded-lg">
                    <div className="text-xl font-bold text-muted-foreground">{pastActivities.length}</div>
                    <div className="text-[10px] text-muted-foreground">Past</div>
                  </div>
                </div>
                
                {/* Selected date info */}
                {selectedDate && (
                  <div className="mt-4 pt-4 border-t">
                    {(() => {
                      const event = getEventForDate(selectedDate);
                      if (event) return (
                        <div className="p-3 bg-destructive/10 rounded-lg border border-destructive/20">
                          <div className="flex items-center gap-2 mb-1">
                            <AlertCircle className="w-4 h-4 text-destructive" />
                            <span className="text-sm font-semibold text-destructive">Date Booked</span>
                          </div>
                          <p className="text-sm font-medium">{event.title}</p>
                          <p className="text-xs text-muted-foreground">{event.association_name}</p>
                        </div>
                      );
                      return (
                        <div className="p-3 bg-cameroon-green/10 rounded-lg border border-cameroon-green/20">
                          <div className="flex items-center gap-2 mb-1">
                            <Check className="w-4 h-4 text-cameroon-green" />
                            <span className="text-sm font-semibold text-cameroon-green">Available</span>
                          </div>
                          <p className="text-xs text-muted-foreground">{formatDate(selectedDate)}</p>
                          <Button size="sm" className="bg-cameroon-green hover:bg-cameroon-green/90 mt-2 w-full" onClick={() => { setFormData(prev => ({...prev, date: selectedDate})); setBookingDialogOpen(true); }}>
                            <Plus className="w-3 h-3 mr-1" /> Book This Date
                          </Button>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Events List */}
          <div className={`lg:col-span-2 ${viewMode === 'calendar' ? 'hidden' : 'block'} sm:block`}>
            {loading ? (
              <div className="space-y-4">
                {Array(3).fill(0).map((_, i) => (
                  <Card key={i} className="animate-pulse">
                    <CardContent className="p-0">
                      <div className="h-48 bg-muted"></div>
                      <div className="p-4 space-y-3">
                        <div className="h-6 bg-muted rounded w-3/4"></div>
                        <div className="h-4 bg-muted rounded w-1/2"></div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="space-y-8">
                {/* Featured Events Banner */}
                {featuredUpcoming.length > 0 && (
                  <div data-testid="featured-events">
                    <h3 className="font-serif text-lg font-semibold mb-4 flex items-center gap-2">
                      <Star className="w-5 h-5 text-cameroon-yellow fill-cameroon-yellow" />
                      Featured Events
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {featuredUpcoming.slice(0, 4).map(event => (
                        <Link key={event.id} to={`/events/${event.id}`} className="block" data-testid={`featured-${event.id}`}>
                          <Card className="overflow-hidden hover:shadow-xl transition-all border-cameroon-yellow/30 hover:border-cameroon-yellow">
                            <CardContent className="p-0">
                              <div className="relative h-40">
                                <img src={event.image_url || event.association_image || 'https://images.unsplash.com/photo-1763256293604-326f823c3bb7?crop=entropy&cs=srgb&fm=jpg&q=85'} alt={event.title} className="w-full h-full object-cover" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                                <Badge className="absolute top-3 right-3 bg-cameroon-yellow text-black"><Star className="w-3 h-3 mr-1 fill-current" />Featured</Badge>
                                <div className="absolute bottom-3 left-3 right-3">
                                  <h4 className="font-serif font-bold text-white text-lg line-clamp-1">{event.title}</h4>
                                  <div className="flex items-center gap-3 mt-1 text-xs text-white/70">
                                    <span className="flex items-center gap-1"><CalendarIcon className="w-3 h-3" />{formatShortDate(event.date)}</span>
                                    {event.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{event.location}</span>}
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upcoming Events */}
                <div data-testid="upcoming-events">
                  <h3 className="font-serif text-lg font-semibold mb-4 flex items-center gap-2">
                    <CalendarIcon className="w-5 h-5 text-cameroon-green" />
                    Upcoming Events
                    <Badge variant="outline" className="ml-1">{upcomingActivities.length}</Badge>
                  </h3>
                  
                  {upcomingActivities.length > 0 ? (
                    <div className="space-y-6">
                      {Object.entries(upcomingByMonth).sort().map(([month, monthActivities]) => (
                        <div key={month}>
                          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2 sticky top-16 bg-background py-2 z-10 text-muted-foreground">
                            {months[parseInt(month.split('-')[1]) - 1]} {month.split('-')[0]}
                            <Badge variant="outline" className="text-xs">{monthActivities.length}</Badge>
                          </h4>
                          <div className="space-y-3">
                            {monthActivities.map((event) => (
                              <Link key={event.id} to={`/events/${event.id}`} className="block" data-testid={`event-link-${event.id}`}>
                                <Card className="overflow-hidden hover:shadow-lg transition-all hover:border-cameroon-green/50 cursor-pointer">
                                  <CardContent className="p-0">
                                    <div className="flex flex-col sm:flex-row">
                                      <div className="relative sm:w-48 lg:w-56 h-48 sm:h-auto flex-shrink-0">
                                        <img src={event.image_url || event.association_image || 'https://images.unsplash.com/photo-1763256293604-326f823c3bb7?crop=entropy&cs=srgb&fm=jpg&q=85'} alt={event.title} className="w-full h-full object-cover" />
                                        <div className="absolute inset-0 bg-gradient-to-t sm:bg-gradient-to-r from-black/60 via-transparent to-transparent" />
                                        <div className="absolute top-3 left-3 sm:hidden bg-white rounded-lg p-2 text-center shadow-lg">
                                          <div className="text-2xl font-bold text-cameroon-green">{event.date.split('-')[2]}</div>
                                          <div className="text-xs text-muted-foreground uppercase">{months[parseInt(event.date.split('-')[1]) - 1].slice(0, 3)}</div>
                                        </div>
                                        <Badge className={`absolute top-3 right-3 ${getCategoryColor(event.category)}`}>{event.category}</Badge>
                                      </div>
                                      <div className="flex-1 p-4 sm:p-5">
                                        <div className="flex items-start gap-4">
                                          <div className="hidden sm:flex flex-col items-center justify-center bg-cameroon-green/10 rounded-lg p-3 min-w-[70px]">
                                            <div className="text-2xl font-bold text-cameroon-green">{event.date.split('-')[2]}</div>
                                            <div className="text-xs text-muted-foreground uppercase">{months[parseInt(event.date.split('-')[1]) - 1].slice(0, 3)}</div>
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <h4 className="font-serif font-semibold text-lg sm:text-xl line-clamp-2">{event.title}</h4>
                                            <div className="flex items-center gap-2 mt-2">
                                              <div className="w-6 h-6 rounded-full bg-cameroon-green/20 flex items-center justify-center flex-shrink-0">
                                                <span className="text-xs font-bold text-cameroon-green">{event.association_short_name?.charAt(0) || 'C'}</span>
                                              </div>
                                              <span className="text-sm text-muted-foreground truncate">{event.association_name}</span>
                                            </div>
                                            <p className="text-muted-foreground text-sm mt-3 line-clamp-2">{event.description}</p>
                                            <div className="flex flex-wrap gap-x-4 gap-y-2 mt-4 text-sm text-muted-foreground">
                                              {event.time && <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-cameroon-yellow" />{event.time}{event.end_time && ` - ${event.end_time}`}</span>}
                                              {event.location && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-cameroon-red" /><span className="truncate max-w-[200px]">{event.location}</span></span>}
                                              {event.end_date && event.end_date !== event.date && (
                                                <span className="flex items-center gap-1.5"><CalendarIcon className="w-4 h-4 text-cameroon-green" />{formatShortDate(event.date)} - {formatShortDate(event.end_date)}</span>
                                              )}
                                            </div>
                                            {event.notice && (
                                              <div className="mt-4 p-3 bg-cameroon-yellow/10 border border-cameroon-yellow/20 rounded-lg">
                                                <div className="flex items-start gap-2">
                                                  <Info className="w-4 h-4 text-cameroon-yellow flex-shrink-0 mt-0.5" />
                                                  <p className="text-sm text-muted-foreground">{event.notice}</p>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>
                              </Link>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Card>
                      <CardContent className="py-16 text-center">
                        <CalendarIcon className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                        <h3 className="font-serif text-xl font-semibold mb-2">No Upcoming Events</h3>
                        <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                          Be the first to book an event! Select a date and submit your association&apos;s cultural activity.
                        </p>
                        <Button onClick={() => setBookingDialogOpen(true)} className="bg-cameroon-green hover:bg-cameroon-green/90">
                          <Plus className="w-4 h-4 mr-2" /> Book First Event
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Past Events (Archived) */}
                {pastActivities.length > 0 && (
                  <div data-testid="past-events">
                    <button
                      onClick={() => setShowPast(!showPast)}
                      className="w-full flex items-center justify-between py-3 px-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                    >
                      <span className="font-serif text-lg font-semibold flex items-center gap-2">
                        <Archive className="w-5 h-5 text-muted-foreground" />
                        Past Events
                        <Badge variant="secondary">{pastActivities.length}</Badge>
                      </span>
                      {showPast ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </button>
                    
                    {showPast && (
                      <div className="space-y-3 mt-4">
                        {pastActivities.map((event) => (
                          <Link key={event.id} to={`/events/${event.id}`} className="block" data-testid={`past-event-${event.id}`}>
                            <Card className="overflow-hidden opacity-75 hover:opacity-100 transition-all">
                              <CardContent className="p-0">
                                <div className="flex items-center gap-4 p-4">
                                  <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
                                    <img src={event.image_url || event.association_image || 'https://images.unsplash.com/photo-1763256293604-326f823c3bb7?crop=entropy&cs=srgb&fm=jpg&q=85'} alt="" className="w-full h-full object-cover grayscale" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h4 className="font-semibold line-clamp-1">{event.title}</h4>
                                    <p className="text-sm text-muted-foreground">{event.association_name}</p>
                                    <p className="text-xs text-muted-foreground mt-1">{formatDate(event.date)}</p>
                                  </div>
                                  <Badge variant="secondary" className={`flex-shrink-0 ${event.status === 'completed' ? 'bg-blue-100 text-blue-700' : ''}`}>
                                    {event.status === 'completed' ? 'Completed' : 'Past'}
                                  </Badge>
                                </div>
                              </CardContent>
                            </Card>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

// Add DialogDescription component import fix
const DialogDescription = ({ children, className }) => (
  <p className={`text-sm text-muted-foreground ${className || ''}`}>{children}</p>
);

export default CalendarPage;
